import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuLabel,
	ContextMenuSeparator,
	ContextMenuTrigger,
} from '@journ/ui/context-menu'
import { tv } from '@journ/ui/cn'
import { useIntlayer } from 'react-intlayer'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import { useMessageActions } from '../hooks/use-message-actions'
import { getReplyInfo, type Message } from '../domain/chat'

type MessageBubbleProps = {
	message: Message
	own: boolean
	showAuthor: boolean
}

const bubble = tv({
	base: 'relative max-w-[75%] whitespace-pre-wrap wrap-break-word rounded-md px-3 py-2 text-sm leading-relaxed',
	variants: {
		own: {
			true: 'bg-primary text-primary-foreground',
			false: 'bg-secondary text-secondary-foreground',
		},
	},
})

const bodyText = (content: Record<string, unknown>): string => (typeof content.txt === 'string' ? content.txt : '')

const ReplyPreview = ({ name, text }: { name: string; text: string }) => (
	<div className='mb-1.5 flex flex-col border-l-2 border-primary/40 pl-2 text-xs'>
		<span className='font-medium'>{name}</span>
		<span className='truncate opacity-80'>{text}</span>
	</div>
)

const MessageBubble = ({ message, own, showAuthor }: MessageBubbleProps) => {
	const c = useIntlayer('chat')
	const { formatPreferenceDateTime } = useDateFormat()
	const { setReplyTarget } = useMessageActions()
	const replyInfo = getReplyInfo(message.head)
	const handleReply = () => setReplyTarget(message)
	const sentAt = formatPreferenceDateTime(message.createdAt)

	return (
		<ContextMenu>
			<ContextMenuTrigger asChild>
				<div className={own ? 'flex flex-col items-end' : 'flex flex-col items-start'}>
					{showAuthor && !own && (
						<span className='mb-1 px-1 text-xs font-medium text-muted-foreground'>{message.fromName}</span>
					)}
					<div className={bubble({ own })}>
						{replyInfo && <ReplyPreview name={replyInfo.name} text={replyInfo.text} />}
						{bodyText(message.content)}
					</div>
					<time dateTime={message.createdAt} className='mt-0.5 px-1 text-[10px] text-muted-foreground'>
						{sentAt}
					</time>
				</div>
			</ContextMenuTrigger>
			<ContextMenuContent>
				<ContextMenuLabel className='text-xs text-muted-foreground font-normal'>
					{c.messageSentAt} · {sentAt}
				</ContextMenuLabel>
				<ContextMenuSeparator />
				<ContextMenuItem onSelect={handleReply}>{c.reply}</ContextMenuItem>
				<ContextMenuItem disabled>{c.forward}</ContextMenuItem>
			</ContextMenuContent>
		</ContextMenu>
	)
}

export { MessageBubble }
